import {get as getBase} from "lodash";
import {
  TranslationsForLocale,
  LocalizeOptions,
  Localize,
  CleanedLocale,
} from "./types";

const get = <
  TObject extends Record<string, unknown>,
  TKey extends keyof TObject
>(
  object: TObject,
  path: TKey
): TObject[TKey] | TKey => getBase(object, path, path);

type TranslationToken = RegExpExecArray;

/**
 * Finds and returns all interpolated strings in a translation
 * Ex: "hello {{firstName}} {{lastName}}" =>
 * [["{{firstName}}", "firstName"], {{lastName}}, "lastName"]
 */
function getInterpTokens(translation: string): TranslationToken[] {
  const interpRegExp = new RegExp("{{(.+?)}}", "g");
  let match: TranslationToken | null;
  const matches: RegExpExecArray[] = [];
  while ((match = interpRegExp.exec(translation))) {
    matches.push(match);
  }
  return matches;
}

function isSingular(count: number, locale: CleanedLocale): boolean {
  // For each locale, specifies the values that are considered sigular
  const singularRules = {
    "en-US": [1],
    "en-CA": [1],
    "fr-CA": [0, 1],
  };
  return singularRules[locale].includes(count);
}

/**
 * Maps the translation key to a translation.
 * If `count` is provided, then selects the correct plural form translation
 */
function getTranslation(
  translations: TranslationsForLocale,
  key: string,
  {count}: LocalizeOptions,
  locale: CleanedLocale
): string {
  if (count === undefined) return get(translations, key);
  if (isSingular(count, locale)) return get(translations, key);
  return get(translations, `${key}_plural`);
}

function formatTranslation(
  translation: string,
  options: LocalizeOptions
): string {
  const interpTokens = getInterpTokens(translation);
  return interpTokens.reduce((formattedTranslation, [interp, interpKey]) => {
    const interpValue = get(options, interpKey) as string;
    return formattedTranslation.replace(interp, interpValue);
  }, translation);
}

export function makeLocalize(
  translations: TranslationsForLocale,
  locale: CleanedLocale
): Localize {
  const localize: Localize = (key, options = {}) => {
    const translation = getTranslation(translations, key, options, locale);
    return formatTranslation(translation, options);
  };
  return localize;
}
